import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import VerifiedIcon from "@mui/icons-material/Verified";
import DevicesIcon from "@mui/icons-material/Devices";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import BuildIcon from "@mui/icons-material/Build";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { AssetsAPI } from "../api/assets";
import { getAuthToken } from "../utils/authSession";

function getSafeQrImageUrl(asset) {
  if (!asset) return "";
  const raw = String(asset.qr_code_url || "");
  const hasLocalhost = /localhost|127\.0\.0\.1/i.test(raw);
  if (raw && !hasLocalhost) return raw;

  const base = "https://itam.sushaservices.com";
  const assetToken = encodeURIComponent(asset.asset_uid || asset.id);
  const text = asset.qr_payload_url || `${base}/asset-details/${assetToken}`;
  const params = new URLSearchParams({
    text,
    size: "420",
    ecLevel: "H",
    margin: "1",
    format: "png",
    centerImageUrl: `${base}/company-logo.png`,
  });
  return `https://quickchart.io/qr?${params.toString()}`;
}

export default function AssetPublicDetailsPage() {
  const { assetId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [asset, setAsset] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [submittedScan, setSubmittedScan] = useState(null);
  const [scanLocation, setScanLocation] = useState(null);
  const [locationBusy, setLocationBusy] = useState(false);
  const [selfScanPhotos, setSelfScanPhotos] = useState([]);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceError, setMaintenanceError] = useState("");
  const [maintenanceRequested, setMaintenanceRequested] = useState(false);

  const getCurrentLocation = async () => {
    if (!window.isSecureContext) {
      throw new Error("Location requires HTTPS. Open the secure site and try again.");
    }
    if (!navigator?.geolocation) {
      throw new Error("Geolocation is not supported by this browser");
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: Number(pos.coords.latitude.toFixed(7)),
          longitude: Number(pos.coords.longitude.toFixed(7)),
        }),
        (err) => {
          if (err?.code === 1) reject(new Error("Location permission denied"));
          else if (err?.code === 2) reject(new Error("Unable to determine location"));
          else if (err?.code === 3) reject(new Error("Location request timed out. Please try again and allow access when prompted."));
          else reject(new Error("Could not read location"));
        },
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
      );
    });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await AssetsAPI.publicDetails(assetId);
        if (!mounted) return;
        const publicAsset = res?.data?.data || null;
        setAsset(publicAsset);
        // QR labels open this public-details page. Record the event separately
        // so a failed telemetry call never blocks the asset details page.
        void AssetsAPI.recordPublicQrScan(assetId)
          .then((scanRes) => {
            if (!mounted) return;
            const lastScannedAt = scanRes?.data?.data?.last_scanned_at;
            if (lastScannedAt) setAsset((current) => current ? { ...current, last_scanned_at: lastScannedAt } : current);
          })
          .catch(() => {});
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.error || "Unable to load asset details");
        setAsset(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [assetId]);

  useEffect(() => {
    let mounted = true;
    const refreshAuditStatus = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const res = await AssetsAPI.publicDetails(assetId);
        if (mounted) {
          const latest = res?.data?.data || null;
          setAsset((current) => latest ? { ...current, ...latest } : current);
        }
      } catch {
        // Keep the last loaded details; a temporary refresh failure should not hide the page.
      }
    };
    const onVisible = () => { if (document.visibilityState === "visible") void refreshAuditStatus(); };
    window.addEventListener("focus", refreshAuditStatus);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(refreshAuditStatus, 30000);
    return () => {
      mounted = false;
      window.removeEventListener("focus", refreshAuditStatus);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [assetId]);

  const statusColor = useMemo(() => {
    const s = String(asset?.asset_status || "").toUpperCase();
    if (["ASSIGNED", "ALLOCATED"].includes(s)) return "success";
    if (["IN_REPAIR"].includes(s)) return "warning";
    if (["RETIRED", "DISPOSED", "SCRAP"].includes(s)) return "default";
    return "primary";
  }, [asset?.asset_status]);
  const scanSubmitted = !!asset?.active_audit_run_id && submittedScan?.assetId === assetId && submittedScan?.runId === asset.active_audit_run_id;
  const locationReady = scanLocation?.assetId === assetId && scanLocation?.runId === asset?.active_audit_run_id;

  const loggedIn = !!getAuthToken();
  const fullDetailsPath = "/enterprise/assets";
  const fullDetailsTarget = loggedIn
    ? fullDetailsPath
    : `/login?returnTo=${encodeURIComponent(fullDetailsPath)}`;

  const setupAuditLocation = async () => {
    setLocationBusy(true);
    setActionError("");
    try {
      const location = await getCurrentLocation();
      setScanLocation({ ...location, assetId, runId: asset?.active_audit_run_id });
    } catch (err) {
      setScanLocation(null);
      setActionError(err?.message || "Unable to set up location");
    } finally {
      setLocationBusy(false);
    }
  };

  const submitSelfScan = async () => {
    setActionBusy(true);
    setActionError("");
    setActionMessage("");
    try {
      const location = await getCurrentLocation();
      const payload = new FormData();
      payload.append("latitude", location.latitude);
      payload.append("longitude", location.longitude);
      selfScanPhotos.slice(0, 3).forEach((file) => payload.append("images", file));
      const res = await AssetsAPI.publicAuditSelfScan(assetId, payload);
      setActionMessage("Audit scan submitted. An administrator will review it.");
      setSubmittedScan({ assetId, runId: res?.data?.data?.audit_run_id || asset?.active_audit_run_id });
      setScanLocation(null);
      setSelfScanPhotos([]);
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.message || "Failed to submit self-scan");
    } finally {
      setActionBusy(false);
    }
  };

  const lockGeofence = async () => {
    setActionBusy(true);
    setActionError("");
    setActionMessage("");
    try {
      const location = await getCurrentLocation();
      await AssetsAPI.publicGeofenceLock(assetId, {
        latitude: location.latitude,
        longitude: location.longitude,
        radius_meters: 100,
      });
      setAsset((prev) => (prev ? { ...prev, geofence_locked: true } : prev));
      setActionMessage("Geofence locked successfully (one-time lock).");
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.message || "Failed to lock geofence");
    } finally {
      setActionBusy(false);
    }
  };

  const requestMaintenance = async () => {
    setMaintenanceBusy(true);
    setMaintenanceError("");
    setMaintenanceMessage("");
    try {
      await AssetsAPI.publicMaintenanceRequest(assetId, {});
      setMaintenanceMessage("Maintenance request submitted successfully.");
      setMaintenanceRequested(true);
    } catch (err) {
      setMaintenanceError(err?.response?.data?.error || err?.message || "Failed to submit maintenance request");
    } finally {
      setMaintenanceBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F1F5F9",
        backgroundImage: "radial-gradient(circle at top right, #DBEAFE 0%, transparent 40%)",
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #D1DCEB", overflow: "hidden", boxShadow: "0 8px 32px rgba(15,23,42,0.08)" }}>
          <Box sx={{ p: 2.5, bgcolor: "#0B3B78", color: "white" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Inventory2Icon />
              <Typography variant="h6" fontWeight={700}>
                Asset Public Details
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Scan verified by Susha Asset Management
            </Typography>
          </Box>

          <Box sx={{ p: 2.5, bgcolor: "#FFFFFF" }}>
            {loading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
                  Loading asset details...
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : !asset ? (
              <Alert severity="warning">Asset details not available.</Alert>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={800} color="#0F172A">
                    {asset.asset_uid || `Asset ${asset.id}`}
                  </Typography>
                  <Chip size="small" color={statusColor} label={asset.asset_status || "UNKNOWN"} />
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip icon={<QrCode2Icon />} size="small" label={asset.asset_type || "OTHER"} />
                  <Chip icon={<VerifiedIcon />} size="small" variant="outlined" label={asset.delivery_status || "N/A"} />
                  {asset.device?.hostname ? (
                    <Chip icon={<DevicesIcon />} size="small" variant="outlined" label={`Device: ${asset.device.hostname}`} />
                  ) : null}
                </Stack>

                <Divider />

                {getSafeQrImageUrl(asset) ? (
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                    <Box
                      component="img"
                      src={getSafeQrImageUrl(asset)}
                      alt={`QR ${asset.asset_uid || asset.id}`}
                      sx={{
                        width: 128,
                        height: 128,
                        border: "1px solid #E2E8F0",
                        borderRadius: 1.5,
                        p: 0.6,
                        bgcolor: "white",
                      }}
                    />
                  </Box>
                ) : null}

                <Stack spacing={1}>
                  <Typography variant="body2"><strong>Vendor:</strong> {asset.vendor_name || "N/A"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Owner, custodian, device, location, and financial details are visible only to authorized users.
                  </Typography>
                </Stack>

                {asset.has_active_audit && !scanSubmitted ? (
                  <Box
                    sx={{
                      border: "1px solid #E2E8F0",
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: "#F8FAFC",
                    }}
                  >
                    <Stack spacing={1}>
                      <Chip size="small" color="success" label="Audit in progress" sx={{ alignSelf: "flex-start", fontWeight: 700 }} />
                      <Typography variant="body2" color="text.secondary">
                        Confirm this asset is present. Your current location is attached to the scan for review.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={setupAuditLocation}
                        disabled={locationBusy || actionBusy}
                        startIcon={locationBusy ? <CircularProgress size={16} /> : <LocationOnIcon />}
                        sx={{ textTransform: "none", alignSelf: "flex-start" }}
                      >
                        {locationBusy ? "Getting location..." : locationReady ? "Refresh location" : "Set up geolocation for this scan"}
                      </Button>
                      {locationReady ? <Alert severity="success">Location permission is ready. We will capture a fresh position when you submit.</Alert> : null}
                      <Typography variant="subtitle2" fontWeight={700}>Optional audit photos</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add up to 3 photos to help the admin review the asset. Photos are retained for 6 months.
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AddPhotoAlternateIcon />}
                        sx={{ textTransform: "none", alignSelf: "flex-start" }}
                      >
                        Attach photos
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => setSelfScanPhotos(Array.from(e.target.files || []).slice(0, 3))}
                        />
                      </Button>
                      {selfScanPhotos.length > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {selfScanPhotos.length} photo{selfScanPhotos.length > 1 ? "s" : ""} selected
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                ) : null}

                <Divider />

                {actionError ? <Alert severity="error">{actionError}</Alert> : null}
                {actionMessage ? <Alert severity="success">{actionMessage}</Alert> : null}
                {maintenanceError ? <Alert severity="error">{maintenanceError}</Alert> : null}
                {maintenanceMessage ? <Alert severity="success">{maintenanceMessage}</Alert> : null}

                {asset.has_active_audit && !scanSubmitted ? (
                  <Button
                    variant="contained"
                    disabled={actionBusy || locationBusy}
                    onClick={submitSelfScan}
                    startIcon={actionBusy ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                      alignSelf: "stretch",
                      bgcolor: "#166534",
                      "&:hover": { bgcolor: "#14532d" },
                      textTransform: "none",
                      fontWeight: 700,
                      py: 1.1,
                    }}
                  >
                    {actionBusy ? "Submitting audit scan..." : "Start audit scan"}
                  </Button>
                ) : null}

                {asset.public_actions_enabled && !asset.geofence_locked ? (
                  <Button
                    variant="outlined"
                    disabled={actionBusy}
                    onClick={lockGeofence}
                    sx={{ textTransform: "none", fontWeight: 700, py: 1.05 }}
                  >
                    Set One-Time Geofence Lock
                  </Button>
                ) : asset.public_actions_enabled ? (
                  <Chip
                    size="small"
                    label="Geofence Lock Active"
                    sx={{
                      alignSelf: "flex-start",
                      bgcolor: "#dbeafe",
                      color: "#1d4ed8",
                      border: "1px solid #93c5fd",
                      fontWeight: 700,
                      px: 0.5,
                    }}
                  />
                ) : null}

                {asset.public_actions_enabled ? <Button
                  variant="outlined"
                  disabled={maintenanceBusy || maintenanceRequested}
                  onClick={requestMaintenance}
                  startIcon={<BuildIcon />}
                  sx={{ textTransform: "none", fontWeight: 700, py: 1.05 }}
                >
                  {maintenanceRequested ? "Maintenance requested" : "Call for Maintenance"}
                </Button> : null}

                <Divider />

                {loggedIn ? (
                  <Button
                    component={Link}
                    to={fullDetailsTarget}
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      alignSelf: "stretch",
                      bgcolor: "#0B3B78",
                      "&:hover": { bgcolor: "#082F61" },
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    View Full Details in Dashboard
                  </Button>
                ) : null}
              </Stack>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
